// frontend/src/components/UserManagement.js - Themed version with professional design

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../axiosConfig';
import {
  Users,
  Search,
  Filter,
  Edit,
  Ban,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  AlertTriangle,
  Shield,
  Car,
  User as UserIcon,
  Key,
  Mail
} from 'lucide-react';
import { formatDate } from '../utils/dateTimeFormatter';
import {
  ROLE_HIERARCHY,
  getAssignableRoleValues,
  getRoleLevel,
  hasRoleLevel,
  isAdmin,
  isSystemAdmin
} from '../utils/roleUtils';
import { maskEmail } from '../utils/emailMasker';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import InviteUsers from './InviteUsers';
import RelationshipManagement from './RelationshipManagement';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedCard, ThemedInput } from './themed';

const UserManagement = ({ currentEntity, userRole }) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();
  // Force re-render when preferences change
  useDateTimePreferences();
  const [activeTab, setActiveTab] = useState('users');
  
  // Get current user information
  const currentUser = React.useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = parseInt(localStorage.getItem('userId'), 10);
      return {
        ...userData,
        id: userId || userData.id,
        role: userRole // Add the current user's role from props
      };
    } catch (error) {
      console.error('Error parsing user data:', error);
      return { 
        id: parseInt(localStorage.getItem('userId'), 10),
        role: userRole // Add the current user's role from props
      };
    }
  }, [userRole]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [joinedDateFilter, setJoinedDateFilter] = useState('all');
  const [joinedDateStart, setJoinedDateStart] = useState('');
  const [joinedDateEnd, setJoinedDateEnd] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

  const roleIcons = {
    root_admin: Shield,
    sys_admin: Shield,
    super_admin: Shield,
    power_admin: Shield,
    admin: Shield,
    driver_admin: Car,
    driver: Car,
    user: UserIcon
  };

  const buildRoleOptions = (roleValues) => roleValues.map(role => ({
    value: role,
    label: t(role),
    icon: roleIcons[role] || UserIcon
  }));

  // Role values come from the shared hierarchy utility; icon components stay
  // local because they are presentation metadata, not authorization policy.
  const allRoles = buildRoleOptions([...ROLE_HIERARCHY].reverse());
  const roles = buildRoleOptions(getAssignableRoleValues(userRole));

  const statuses = [
    { value: 'active', label: t('active'), color: 'green' },
    { value: 'inactive', label: t('inactive'), color: 'gray' },
    { value: 'suspended', label: t('suspended'), color: 'yellow' },
    { value: 'revoked', label: t('revoked'), color: 'red' }
  ];

  // Users can access invitations but not user management
  const canManageUsers = isAdmin(userRole);
  const canInviteUsers = true; // All authenticated users can invite
  const canManageRelationships = true; // All authenticated users can manage their relationships

  const fetchUsers = useCallback(async () => {
    if (!currentEntity || !currentEntity.entityId) {
      console.warn('No current entity available');
      setLoading(false);
      return;
    }

    if (!isAdmin(userRole)) {
      console.warn('User is not an admin');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `/api/settings/entity/${currentEntity.entityId}/users?includeInactive=true`
      );
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      toast.error(t('errorFetchingUsers'));
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentEntity, t, userRole]);

  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => user.entity_status === filterStatus);
    }

    // Joined date filter
    if (joinedDateFilter !== 'all') {
      const now = new Date();
      let startDate, endDate;
      
      switch (joinedDateFilter) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'custom':
          if (joinedDateStart) startDate = new Date(joinedDateStart);
          if (joinedDateEnd) endDate = new Date(joinedDateEnd + 'T23:59:59');
          break;
        default:
          break;
      }
      
      if (startDate || endDate) {
        filtered = filtered.filter(user => {
          const userJoinedDate = new Date(user.created_at);
          if (startDate && endDate) {
            return userJoinedDate >= startDate && userJoinedDate <= endDate;
          } else if (startDate) {
            return userJoinedDate >= startDate;
          } else if (endDate) {
            return userJoinedDate <= endDate;
          }
          return true;
        });
      }
    }

    return filtered;
  }, [users, searchTerm, filterRole, filterStatus, joinedDateFilter, joinedDateStart, joinedDateEnd]);

  useEffect(() => {
    if (currentEntity && currentEntity.entityId) {
      setLoading(true);
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [currentEntity, fetchUsers]);

  // Set default tab based on permissions
  useEffect(() => {
    if (!canManageUsers && canInviteUsers) {
      setActiveTab('invites');
    }
  }, [canManageUsers, canInviteUsers]);

  const handleRoleChange = async (userId, newRole) => {
    if (!currentEntity || !currentEntity.entityId) {
      toast.error(t('noEntitySelected'));
      return;
    }
    
    try {
      const response = await axios.put(
        `/api/settings/entity/${currentEntity.entityId}/users/${userId}/role`,
        { role: newRole }
      );

      if (response.data.success) {
        toast.success(t('userRoleUpdatedSuccessfully'));
        fetchUsers();
        setShowEditModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUpdatingUserRole'));
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    if (!currentEntity || !currentEntity.entityId) {
      toast.error(t('noEntitySelected'));
      return;
    }
    
    try {
      const response = await axios.put(
        `/api/settings/entity/${currentEntity.entityId}/users/${userId}/status`,
        { status: newStatus }
      );

      if (response.data.success) {
        toast.success(t('userStatusUpdatedSuccessfully'));
        fetchUsers();
        setShowStatusModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUpdatingUserStatus'));
    }
  };

  const handlePasswordReset = async (userId) => {
    if (!currentEntity || !currentEntity.entityId) {
      toast.error(t('noEntitySelected'));
      return;
    }

    try {
      const response = await axios.put(
        `/api/settings/entity/${currentEntity.entityId}/users/${userId}/reset-password`
      );

      if (response.data.success) {
        // Show success with masked email (email link flow)
        toast.success(t('resetLinkSentSuccess', { maskedEmail: response.data.maskedEmail }));
        setShowPasswordResetModal(false);
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorResettingPassword'));
    }
  };

  const getRoleIcon = (role) => {
    // First check in the filtered roles array
    const roleConfig = roles.find(r => r.value === role);
    if (roleConfig) return roleConfig.icon;
    
    // If not found, check in allRoles (for roles current user can't manage)
    const allRoleConfig = allRoles.find(r => r.value === role);
    return allRoleConfig ? allRoleConfig.icon : UserIcon;
  };

  const getStatusColor = (status) => {
    const statusConfig = statuses.find(s => s.value === status);
    return statusConfig ? statusConfig.color : 'gray';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return CheckCircle;
      case 'inactive':
        return XCircle;
      case 'suspended':
        return AlertTriangle;
      case 'revoked':
        return Ban;
      default:
        return XCircle;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentEntity || !currentEntity.entityId) {
    return (
      <div className="text-center py-8">
        <p className={cn("text-gray-500 dark:text-gray-400")}>{t('noEntitySelected')}</p>
      </div>
    );
  }

  if (!canInviteUsers && !canManageRelationships) {
    return (
      <div className="text-center py-8">
        <p className={cn("text-gray-500 dark:text-gray-400")}>{t('noPermissionToAccessUserFeatures')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn("text-2xl font-bold", text.primary)}>
          {canManageUsers ? t('userManagement') : t('inviteUsers')}
        </h2>
      </div>

      {/* Tab Navigation */}
      <div className={cn("border-b", "border-gray-200 dark:border-gray-700")}>
        <nav className="-mb-px flex space-x-8">
          {canManageUsers && (
            <button
              onClick={() => setActiveTab('users')}
              className={cn(
                "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
                activeTab === 'users'
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
              )}
            >
              <Users className="inline w-4 h-4 mr-2" />
              {t('manageUsers')} ({filteredUsers.length}/{users.length})
            </button>
          )}
          {canInviteUsers && (
            <button
              onClick={() => setActiveTab('invites')}
              className={cn(
                "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
                activeTab === 'invites'
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
              )}
            >
              <Mail className="inline w-4 h-4 mr-2" />
              {t('inviteUsers')}
            </button>
          )}
          {canManageRelationships && (
            <button
              onClick={() => setActiveTab('relationships')}
              className={cn(
                "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
                activeTab === 'relationships'
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
              )}
            >
              <Users className="inline w-4 h-4 mr-2" />
              {isAdmin(userRole) ? t('userRelationships') : t('myRelationships')}
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'relationships' ? (
        <RelationshipManagement currentUser={currentUser} currentEntity={currentEntity} />
      ) : activeTab === 'invites' || !canManageUsers ? (
        <InviteUsers currentEntity={currentEntity} userRole={userRole} />
      ) : (
        <div className="space-y-6">
          {/* Filters and Search */}
          <ThemedCard>
            <ThemedCard.Body>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <ThemedInput
                  label={
                    <>
                      <Search className="inline w-4 h-4 mr-1" />
                      {t('search')}
                    </>
                  }
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('searchUsers')}
                />

                <div>
                  <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                    <Filter className="inline w-4 h-4 mr-1" />
                    {t('filterByRole')}
                  </label>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md transition-all duration-200",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                      "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                  >
                    <option value="all">{t('allRoles')}</option>
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                    <Filter className="inline w-4 h-4 mr-1" />
                    {t('filterByStatus')}
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md transition-all duration-200",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                      "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                  >
                    <option value="all">{t('allStatuses')}</option>
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={cn("block text-sm font-medium mb-1", text.secondary)}>
                    <Filter className="inline w-4 h-4 mr-1" />
                    {t('filterByJoinedDate')}
                  </label>
                  <select
                    value={joinedDateFilter}
                    onChange={(e) => setJoinedDateFilter(e.target.value)}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md transition-all duration-200",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                      "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                  >
                    <option value="all">{t('allDates')}</option>
                    <option value="last7days">{t('last7Days')}</option>
                    <option value="last30days">{t('last30Days')}</option>
                    <option value="last90days">{t('last90Days')}</option>
                    <option value="custom">{t('customRange')}</option>
                  </select>
                </div>
              </div>
            </ThemedCard.Body>
          </ThemedCard>

          {/* Custom Date Range */}
          {joinedDateFilter === 'custom' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('startDate')}
              </label>
              <input
                type="date"
                value={joinedDateStart}
                onChange={(e) => setJoinedDateStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('endDate')}
              </label>
              <input
                type="date"
                value={joinedDateEnd}
                onChange={(e) => setJoinedDateEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}
          {/* Users Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('joinedDate')}
                  </th>

                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      {t('noUsersFound')}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const RoleIcon = getRoleIcon(user.role);
                    const StatusIcon = getStatusIcon(user.entity_status);
                    const statusColor = getStatusColor(user.entity_status);

                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              disabled={isSystemAdmin(user.role) && !isSystemAdmin(userRole)}
                              title={t('editUserRole')}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowStatusModal(true);
                              }}
                              className="text-gray-600 hover:text-gray-900"
                              title={user.entity_status === 'active' ? t('deactivateUser') : t('activateUser')}
                            >
                              {user.entity_status === 'active' ? (
                                <UserX className="w-4 h-4" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowPasswordResetModal(true);
                              }}
                              className="text-orange-600 hover:text-orange-900"
                              title={t('resetUserPassword')}
                            >
                              <Key className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{user.username}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <RoleIcon className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {allRoles.find(r => r.value === user.role)?.label || user.role}
                            </span>
                          </div>
                          {user.is_primary && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                              {t('primaryEntity')}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <StatusIcon className={`w-4 h-4 mr-2 text-${statusColor}-500`} />
                            <span className={`text-sm text-${statusColor}-600`}>
                              {statuses.find(s => s.value === user.entity_status)?.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.created_at)}
                        </td>                    
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('changeUserRole')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('changeRoleFor', { name: `${selectedUser.first_name} ${selectedUser.last_name}` })}
            </p>
            
            <div className="space-y-2">
              {roles.map((role) => {
                const RoleIcon = role.icon;
                // Check if user can assign this role using roleUtils
                const canManage = hasRoleLevel(userRole, 'super_admin') || 
                  getRoleLevel(role.value) <= getRoleLevel(userRole);
                const isDisabled = !canManage || getRoleLevel(userRole) === -1;
                
                return (
                  <label
                    key={role.value}
                    className={`flex items-center p-3 border rounded-md cursor-pointer ${
                      selectedUser.role === role.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={selectedUser.role === role.value}
                      onChange={(e) => !isDisabled && handleRoleChange(selectedUser.id, e.target.value)}
                      disabled={isDisabled}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <RoleIcon className="w-4 h-4 ml-3 mr-2 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{role.label}</span>
                    {isDisabled && (
                      <span className="ml-auto text-xs text-gray-500">{t('insufficientPermissions')}</span>
                    )}
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Status Modal */}
      {showStatusModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('changeUserStatus')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('changeStatusFor', { name: `${selectedUser.first_name} ${selectedUser.last_name}` })}
            </p>
            
            <div className="space-y-2">
              {statuses.map((status) => {
                const StatusIcon = getStatusIcon(status.value);
                
                return (
                  <label
                    key={status.value}
                    className={`flex items-center p-3 border rounded-md cursor-pointer ${
                      selectedUser.entity_status === status.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={status.value}
                      checked={selectedUser.entity_status === status.value}
                      onChange={(e) => handleStatusChange(selectedUser.id, e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <StatusIcon className={`w-4 h-4 ml-3 mr-2 text-${status.color}-500`} />
                    <span className="text-sm font-medium text-gray-900">{status.label}</span>
                  </label>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('resetPassword')}
            </h3>

            {/* Show masked email destination */}
            {selectedUser.email ? (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {t('resetLinkWillBeSentTo', { maskedEmail: maskEmail(selectedUser.email) })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-2" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('userHasNoEmail')}
                  </p>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('confirmPasswordResetEmail', { name: `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || selectedUser.username })}
            </p>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPasswordResetModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => handlePasswordReset(selectedUser.id)}
                disabled={!selectedUser.email}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('sendResetLink')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default UserManagement;
